import {
  toggleReactionSummary,
  type ReactionSummary,
} from "@/domain/feature-announcement-feedback/feedback-reactions"

/**
 * The shape of the cached user thread, narrowed to what the optimistic flip
 * touches. Structurally compatible with MyFeedbackThread.
 */
export type ReactionThreadCache = {
  readonly feedback: { readonly reactions: readonly ReactionSummary[] }
  readonly messages: ReadonlyArray<{ readonly id: string; readonly reactions: readonly ReactionSummary[] }>
}

/**
 * Apply an optimistic reaction flip to a cached thread.
 *
 * `noteId === null` targets the original submission, whose reactions live on
 * `feedback` rather than in `messages` — the same asymmetry the schema absorbs
 * with a nullable note_id, surfacing here as two branches instead of one.
 *
 * Pure and total: an unknown noteId returns the cache untouched rather than
 * throwing, because a stale key would otherwise break the whole thread render.
 */
export function applyReactionToggle<T extends ReactionThreadCache>(
  cache: T,
  noteId: string | null,
  emoji: string,
): T {
  if (noteId === null) {
    return {
      ...cache,
      feedback: { ...cache.feedback, reactions: toggleReactionSummary(cache.feedback.reactions, emoji) },
    }
  }
  return {
    ...cache,
    messages: cache.messages.map((m) =>
      m.id === noteId ? { ...m, reactions: toggleReactionSummary(m.reactions, emoji) } : m,
    ),
  }
}
